/**
 * Main SpinnerWheel component - orchestrates all sub-components
 */

import React, { useCallback, useMemo, useRef } from 'react';
import { SpinnerWheelCanvas } from './SpinnerWheelCanvas';
import { SpinnerWheelEntries } from './SpinnerWheelEntries';
import { SpinnerWheelLabel } from './SpinnerWheelLabel';
import { SpinnerWheelWinnerPopup } from './SpinnerWheelWinnerPopup';
import { useAudio } from '../hooks/useAudio';
import { DEFAULT_ENTRIES } from '../utils/colors';
import type {
  SpinnerWheelProps,
  WheelSegment,
  WheelEntry,
  SpinnerWheelLabelHandle,
} from '../types/wheel';
import './SpinnerWheel.css';

export const SpinnerWheel: React.FC<SpinnerWheelProps> = ({
  // State
  entries,
  isSpinning,
  winner,
  currentSegment,
  showWinnerPopup,

  // Event handlers
  onEntriesChange,
  onSpin,
  onSpinComplete,
  onSegmentChange,
  onWinnerPopupClose,

  // Configuration
  canvasSize = 465,
  variant = 'white',
  layout = 'default',
  showEntryInput = true,
  audioEnabled = true,
  confettiEnabled = true,
  tickPath,
  celebrationPath,
  tickVolume,
  celebrationVolume,

  // Customization
  colors,
  colorTheme = 'default',
  spinDuration = 10,
  spinRevolutions = 7,
  pointerAngle = 309,

  // Style
  className = '',
  style,
}) => {
  const audio = useAudio({
    enabled: audioEnabled,
    tickPath,
    celebrationPath,
    tickVolume,
    celebrationVolume,
  });

  // Imperative handle for the label — lets us update its DOM per Winwheel tick
  // without re-rendering this component (and its children) every frame.
  const labelRef = useRef<SpinnerWheelLabelHandle>(null);

  // Display entries (show defaults if empty)
  const displayEntries = useMemo(() => {
    return entries.length > 0 ? entries : DEFAULT_ENTRIES;
  }, [entries]);

  // Handle spin initiation
  const handleSpin = useCallback(() => {
    if (isSpinning || displayEntries.length === 0) return;
    audio.prepare();
    onSpin();
  }, [isSpinning, displayEntries.length, audio, onSpin]);

  // Handle spin completion
  const handleSpinComplete = useCallback(
    (segment: WheelSegment) => {
      // Find the winner entry
      const winnerEntry = entries.length > 0
        ? entries.find((e) => e.name === segment.fullText)
        : DEFAULT_ENTRIES.find((e) => e.name === segment.fullText);

      if (winnerEntry) {
        // Update current segment with winner's color for the popup
        onSegmentChange?.({
          style: segment.fillStyle,
          text: segment.fullText || segment.text,
        });

        audio.playCelebration();
        onSpinComplete(winnerEntry);
      }
    },
    [entries, audio, onSpinComplete, onSegmentChange]
  );

  // Handle segment change during spin.
  // Mutate the label DOM directly via its imperative handle — going through
  // `onSegmentChange` (and thus React state in the parent) every Winwheel tick
  // was driving 1.77s of composite + 438 style recalcs per spin on Safari.
  // The public `onSegmentChange` prop still fires once at spin completion,
  // from `handleSpinComplete` below.
  const handleSegmentChange = useCallback((segment: WheelSegment) => {
    labelRef.current?.setSegment(
      segment.fullText || segment.text,
      segment.fillStyle
    );
  }, []);

  // Handle adding entries
  const handleAddEntries = useCallback(
    (newEntries: WheelEntry[]) => {
      onEntriesChange([...entries, ...newEntries]);
    },
    [entries, onEntriesChange]
  );

  // Handle removing entry
  const handleRemoveEntry = useCallback(
    (index: number) => {
      const updated = entries.filter((_, i) => i !== index);
      onEntriesChange(updated);
    },
    [entries, onEntriesChange]
  );

  const spinningCls = isSpinning ? 'is-spinning' : '';

  // Table layout with SVG background
  if (layout === 'table') {
    return (
      <div className={`spinner-wheel --${variant} --table ${spinningCls} ${className}`} style={style}>
        <div className="spinner-wheel-table">
          {/* SVG background shape connecting entries to wheel */}
          <svg
            className="spinner-wheel-table__bg"
            width="800"
            height="512"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 502 322"
          >
            {/* Outer shadow shape */}
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M341 322c88.918 0 161-72.082 161-161S429.918 0 341 0c-43.868 0-83.638 17.545-112.677 46H15C6.716 46 0 52.716 0 61v201c0 8.284 6.716 15 15 15h214.353c28.945 27.866 68.295 45 111.647 45z"
              fill="#EFF1F7"
              fillOpacity=".5"
            />
            {/* Inner white shape */}
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M494 161c0 84.5-68.5 153-153 153-42.345 0-80.672-17.202-108.374-45H14a6 6 0 01-6-6V96a6 6 0 016-6h191.436C231.02 41.252 282.127 8 341 8c84.5 0 153 68.5 153 153z"
              fill="#fff"
              fillOpacity=".8"
            />
          </svg>

          {/* Label positioned at top of rectangular part */}
          <div className="spinner-wheel-table__label">
            <SpinnerWheelLabel
              ref={labelRef}
              text={currentSegment.text}
              backgroundColor={currentSegment.style}
              showWinner={showWinnerPopup}
              variant={variant}
            />
          </div>

          {/* Wheel positioned over circular part (right side) */}
          <div className="spinner-wheel-table__wheel">
            <SpinnerWheelCanvas
              entries={displayEntries}
              isSpinning={isSpinning}
              canvasSize={canvasSize}
              pointerAngle={pointerAngle}
              colors={colors}
              colorTheme={colorTheme}
              spinDuration={spinDuration}
              spinRevolutions={spinRevolutions}
              onSpinComplete={handleSpinComplete}
              onSegmentChange={handleSegmentChange}
              onTickSound={audio.playTick}
              onSpin={handleSpin}
            />
          </div>

          {/* Entries section positioned over rectangular part (left side) */}
          {showEntryInput && (
            <div className="spinner-wheel-table__entries">
              <SpinnerWheelEntries
                entries={entries}
                winner={winner}
                isSpinning={isSpinning}
                variant={variant}
                onAdd={handleAddEntries}
                onRemove={handleRemoveEntry}
                className="spinner-wheel-table__list"
              />
            </div>
          )}
        </div>

        {/* Winner popup */}
        <SpinnerWheelWinnerPopup
          show={showWinnerPopup}
          winner={winner}
          backgroundColor={currentSegment.style}
          variant={variant}
          confettiEnabled={confettiEnabled}
          onClose={onWinnerPopupClose}
        />
      </div>
    );
  }

  // Default layout
  return (
    <div className={`spinner-wheel --${variant} ${spinningCls} ${className}`} style={style}>
      <div className="spinner-wheel__layout">
        {/* Left side - Wheel */}
        <div className="spinner-wheel__wheel-section">
          {/* Label display */}
          <SpinnerWheelLabel
            ref={labelRef}
            text={currentSegment.text}
            backgroundColor={currentSegment.style}
            showWinner={showWinnerPopup}
            variant={variant}
          />

          {/* Canvas wheel */}
          <SpinnerWheelCanvas
            entries={displayEntries}
            isSpinning={isSpinning}
            canvasSize={canvasSize}
            pointerAngle={pointerAngle}
            colors={colors}
            colorTheme={colorTheme}
            spinDuration={spinDuration}
            spinRevolutions={spinRevolutions}
            onSpinComplete={handleSpinComplete}
            onSegmentChange={handleSegmentChange}
            onTickSound={audio.playTick}
            onSpin={handleSpin}
          />
        </div>

        {/* Right side - Entry management */}
        {showEntryInput && (
          <div className="spinner-wheel__entries-section">
            <SpinnerWheelEntries
              entries={entries}
              winner={winner}
              isSpinning={isSpinning}
              variant={variant}
              onAdd={handleAddEntries}
              onRemove={handleRemoveEntry}
            />
          </div>
        )}
      </div>

      {/* Winner popup */}
      <SpinnerWheelWinnerPopup
        show={showWinnerPopup}
        winner={winner}
        backgroundColor={currentSegment.style}
        variant={variant}
        confettiEnabled={confettiEnabled}
        onClose={onWinnerPopupClose}
      />
    </div>
  );
};

export default SpinnerWheel;
